/**
 * Authentication Hook
 *
 * Custom React hook for managing authentication state and operations
 * using MSAL (Microsoft Authentication Library).
 *
 * Requirements:
 * - FR-001: Authenticate users via Microsoft Entra ID
 * - FR-002: Support roles: admin, operator, viewer
 * - FR-003: Reject unauthenticated requests
 * - FR-063: Session timeout after 8 hours
 * - FR-064: Maximum session duration of 24 hours
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import {
  InteractionStatus,
  AccountInfo,
  AuthenticationResult,
  SilentRequest,
} from '@azure/msal-browser';
import {
  loginRequest,
  createLoginRequest,
  silentRequest,
  apiTokenRequest,
  tokenConfig,
  isDevAuthBypass,
} from '../../../authConfig';
import { getDevUserRole } from '../../../auth/devUserStore';
import { roleSatisfies } from '@shared/access/roles';

/**
 * 🛑 The Docker build never sets VITE_API_BASE_URL (webapp/Dockerfile sets only the Entra ARGs and .dockerignore
 * excludes .env*), so the raw value interpolated to "/undefined/api/...". The role lookup then hit the SPA's HTML,
 * failed to parse and silently kept the 'viewer' default — prod 2026-09-30: an Admin shown the Viewer dashboard.
 * Same fallback as services/apiClient.ts: an empty base means same-origin.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * User role type from Azure AD claims
 */
export type UserRole = 'admin' | 'operator' | 'viewer';

/**
 * Authenticated user information
 */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  oid: string; // Azure AD Object ID
  tenantId: string;
}

/**
 * Authentication state
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  error: Error | null;
  sessionExpiresAt: Date | null;
}

/**
 * Authentication hook return type
 */
export interface UseAuthReturn extends AuthState {
  login: (loginHint?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<void>;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  canAccess: (permission: string) => boolean;
  canAccessAny: (permissions: string[]) => boolean;
  canAccessAll: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  isOperatorOrAbove: () => boolean;
  isReadOnly: () => boolean;
  getPermissions: () => string[];
}

/**
 * Permission matrix by role
 */
const permissionsByRole: Record<UserRole, string[]> = {
  admin: [
    'view:dashboard',
    'view:backups',
    'view:lakehouses',
    'view:alerts',
    'view:audit',
    'view:settings',
    'execute:backup',
    'acknowledge:alert',
    'modify:schedule',
    'modify:retention',
    'modify:settings',
    'manage:users',
    'manage:deployments',
  ],
  operator: [
    'view:dashboard',
    'view:backups',
    'view:lakehouses',
    'view:alerts',
    'view:audit',
    'view:settings',
    'execute:backup',
    'acknowledge:alert',
  ],
  viewer: ['view:dashboard', 'view:backups', 'view:lakehouses', 'view:alerts', 'view:settings'],
};

/**
 * Spec 285: SuperAdmin ⊇ Admin. The stored role is the lowercased database role, so a SuperAdmin
 * arrives as 'superadmin' — a key this table never had. Look it up as admin.
 */
const permissionRole = (role: string): UserRole =>
  (String(role).toLowerCase() === 'superadmin' ? 'admin' : role) as UserRole;

/**
 * Extract user role from account claims
 */
function extractRole(account: AccountInfo): UserRole {
  const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;

  if (!idTokenClaims) {
    return 'viewer';
  }

  // Check for role in different claim locations
  const roles = idTokenClaims.roles as string[] | undefined;
  const role = idTokenClaims.role as string | undefined;
  const groups = idTokenClaims.groups as string[] | undefined;

  // Check roles array first
  if (roles && roles.length > 0) {
    if (roles.includes('Admin') || roles.includes('admin')) {
      return 'admin';
    }
    if (roles.includes('Operator') || roles.includes('operator')) {
      return 'operator';
    }
    if (roles.includes('Viewer') || roles.includes('viewer')) {
      return 'viewer';
    }
  }

  // Check single role claim
  if (role) {
    const lowerRole = role.toLowerCase();
    if (lowerRole === 'admin') {
      return 'admin';
    }
    if (lowerRole === 'operator') {
      return 'operator';
    }
  }

  // Check group membership (configured in Azure AD)
  const adminGroupId = import.meta.env.VITE_ADMIN_GROUP_ID;
  const operatorGroupId = import.meta.env.VITE_OPERATOR_GROUP_ID;

  if (groups && adminGroupId && groups.includes(adminGroupId)) {
    return 'admin';
  }
  if (groups && operatorGroupId && groups.includes(operatorGroupId)) {
    return 'operator';
  }

  // Default to viewer
  return 'viewer';
}

/**
 * Build AuthUser from MSAL AccountInfo
 */
function buildAuthUser(account: AccountInfo): AuthUser {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;

  return {
    id: account.localAccountId,
    email: account.username,
    displayName: account.name || account.username,
    role: extractRole(account),
    oid: (claims?.oid as string) || account.localAccountId,
    tenantId: account.tenantId,
  };
}

/**
 * Calculate session expiration time
 */
function calculateSessionExpiry(): Date {
  const now = new Date();
  return new Date(now.getTime() + tokenConfig.maxSessionDurationHours * 60 * 60 * 1000);
}

/**
 * Authentication hook
 *
 * Provides authentication state and operations for the application.
 */
export function useAuth(): UseAuthReturn {
  const { instance, inProgress, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);
  const isAuthenticated = useIsAuthenticated();
  
  // Track the account ID for which we've already fetched the backend role
  // to prevent duplicate API calls on re-renders
  const fetchedRoleForAccountRef = useRef<string | null>(null);

  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
    sessionExpiresAt: null,
  });

  // Update authentication state when account changes
  useEffect(() => {
    // Handle development auth bypass mode — mirror the role chosen on the dev login page
    if (isDevAuthBypass) {
      const selectedRole = getDevUserRole();
      if (!selectedRole) {
        // No role chosen yet — keep loading so DevAuthGuard can show the role picker
        setState((prev) => ({ ...prev, isLoading: true, user: null }));
        return;
      }
      const role = selectedRole.toLowerCase() as UserRole;
      const devUser: AuthUser = {
        id: `dev-${role}`,
        email: `${role}@dev.local`,
        displayName: `Dev ${selectedRole}`,
        role,
        oid: `dev-oid-${role}`,
        tenantId: 'dev-tenant',
      };
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: devUser,
        error: null,
        sessionExpiresAt: calculateSessionExpiry(),
      });
      return;
    }

    const isLoading = inProgress !== InteractionStatus.None;

    if (isLoading) {
      setState((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    if (isAuthenticated && account) {
      // Skip if we've already fetched the role for this account
      const accountId = account.localAccountId;
      if (fetchedRoleForAccountRef.current === accountId) {
        return;
      }
      
      // Mark that we're fetching for this account SYNCHRONOUSLY to prevent duplicate calls
      // This must happen before the async fetchBackendRole to prevent race conditions
      fetchedRoleForAccountRef.current = accountId;
      
      // Build initial user from MSAL, then fetch role from backend
      const initialUser = buildAuthUser(account);
      setState({
        isAuthenticated: true,
        isLoading: true, // Keep loading while fetching backend role
        user: initialUser,
        error: null,
        sessionExpiresAt: calculateSessionExpiry(),
      });

      // Fetch the actual role from the backend database.
      //
      // 🛑 Spec 241: resolve the role from GET /api/auth/session — the SAME source the header
      //    badge, the "View as:" switcher, and AuthProvider/useAuthStore already use. It returns
      //    req.user.role directly (the tenant-bound authMiddleware resolves it), with NO per-user
      //    findOrCreateFromAzureAd lookup.
      //
      //    The previous POST /api/v1/auth/login path ran findOrCreateFromAzureAd, whose
      //    tenant-scoped getByEmail can miss at login and create/return a Viewer — and if the
      //    call failed outright, this hook silently kept extractRole()'s 'viewer' default. Either
      //    way the SIDEBAR resolved Viewer while the badge (session) resolved Admin: one Admin
      //    user, two role sources, a split menu. Sourcing both from /api/auth/session removes the
      //    split — the whole app derives one role from one place.
      const fetchBackendRole = async () => {
        try {
          const tokenResult = await instance.acquireTokenSilent({
            ...apiTokenRequest,
            account: account,
          });

          const response = await fetch(`${API_BASE}/api/auth/session`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${tokenResult.idToken}`,
            },
          });

          if (response.ok) {
            const session = await response.json();
            const backendRole = session?.user?.role as string | undefined;
            if (backendRole) {
              const dbRole = backendRole.toLowerCase() as UserRole;
              console.log('Session restored with database role:', dbRole);
              setState((prev) => ({
                ...prev,
                isLoading: false,
                user: prev.user ? { ...prev.user, role: dbRole } : prev.user,
              }));
              return;
            }
          }
        } catch (err) {
          console.warn('Failed to fetch backend role:', err);
        }
        // If backend fetch fails, just clear loading state
        setState((prev) => ({ ...prev, isLoading: false }));
      };

      fetchBackendRole();
    } else {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
        sessionExpiresAt: null,
      });
    }
  }, [isAuthenticated, account, inProgress, instance]);

  // Session timeout check (FR-063, FR-064)
  useEffect(() => {
    if (!state.isAuthenticated || !state.sessionExpiresAt) {
      return;
    }

    const checkSession = (): void => {
      if (state.sessionExpiresAt && new Date() >= state.sessionExpiresAt) {
        console.warn('Session expired, logging out');
        logout();
      }
    };

    const interval = setInterval(checkSession, tokenConfig.sessionCheckIntervalMs);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated, state.sessionExpiresAt]);

  /**
   * Login with popup
   * @param loginHint - Optional email/UPN to pre-fill in the login form
   *                    Following Microsoft best practice: use loginHint to skip
   *                    account selection when user specifies an account.
   */
  const login = useCallback(async (loginHint?: string): Promise<void> => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Create login request with optional loginHint
      const request = createLoginRequest(loginHint);
      const result: AuthenticationResult = await instance.loginPopup(request);

      if (result.account) {
        instance.setActiveAccount(result.account);
        // Mark that we've fetched the role for this account to prevent duplicate API calls
        fetchedRoleForAccountRef.current = result.account.localAccountId;
        
        // Build initial user from MSAL token (role will be updated from backend)
        let user = buildAuthUser(result.account);

        // Get API token (separate from Graph login token) for backend notification
        try {
          console.log('Requesting API token with scopes:', apiTokenRequest.scopes);
          const apiTokenResult = await instance.acquireTokenSilent({
            ...apiTokenRequest,
            account: result.account,
          });
          console.log('API token audience:', apiTokenResult.idToken.split('.')[1]);
          // Decode and log the token payload
          const tokenPayload = JSON.parse(atob(apiTokenResult.idToken.split('.')[1]));
          console.log('API token claims:', {
            aud: tokenPayload.aud,
            scp: tokenPayload.scp,
            roles: tokenPayload.roles,
          });

          const response = await fetch(`${API_BASE}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${apiTokenResult.idToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            // Use the role from the database (returned by backend)
            const backendUser = await response.json();
            console.log('Backend user data:', backendUser);
            if (backendUser.role) {
              const dbRole = backendUser.role.toLowerCase() as UserRole;
              console.log('Using database role:', dbRole);
              user = { ...user, role: dbRole };
            }
          } else {
            console.warn('Backend login notification failed:', response.status);
          }
        } catch (err) {
          console.warn('Failed to notify backend of login:', err);
        }

        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          error: null,
          sessionExpiresAt: calculateSessionExpiry(),
        });
      }
    } catch (error) {
      console.error('Login failed:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Login failed'),
      }));
      throw error;
    }
  }, [instance]);

  /**
   * Logout
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Notify backend of logout
      const token = await getAccessToken();
      if (token) {
        try {
          await fetch(`${API_BASE}/api/v1/auth/logout`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (err) {
          console.warn('Failed to notify backend of logout:', err);
        }
      }

      // Clear MSAL state and redirect
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });

      // Clear the fetched role ref so re-login triggers a fresh fetch
      fetchedRoleForAccountRef.current = null;
      
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
        sessionExpiresAt: null,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  /**
   * Get access token for API calls
   * Uses apiTokenRequest to get token with backend API scope
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const request: SilentRequest = {
        ...apiTokenRequest,
        account,
      };

      const response = await instance.acquireTokenSilent(request);
      return response.idToken; // FCM model — ID token (see authConfig.ts / auth/config.ts)
    } catch (error) {
      console.warn('Silent token acquisition failed, trying popup:', error);

      try {
        const response = await instance.acquireTokenPopup({
          ...apiTokenRequest,
          account,
        });
        return response.idToken; // FCM model — ID token (see authConfig.ts / auth/config.ts)
      } catch (popupError) {
        console.error('Token acquisition failed:', popupError);
        return null;
      }
    }
  }, [instance, account]);

  /**
   * Refresh session
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshSession = useCallback(async (): Promise<void> => {
    const token = await getAccessToken();
    if (token) {
      setState((prev) => ({
        ...prev,
        sessionExpiresAt: calculateSessionExpiry(),
      }));

      // Notify backend
      try {
        await fetch(`${API_BASE}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err) {
        console.warn('Failed to refresh session with backend:', err);
      }
    }
  }, []); // getAccessToken is intentionally omitted to avoid recreation of this callback

  /**
   * Check if user has required role
   */
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!state.user) {
        return false;
      }

      const roles = Array.isArray(role) ? role : [role];
      return roles.includes(permissionRole(state.user.role));
    },
    [state.user]
  );

  /**
   * Check if user has permission
   */
  const canAccess = useCallback(
    (permission: string): boolean => {
      if (!state.user) {
        return false;
      }

      const userPermissions = permissionsByRole[permissionRole(state.user.role)] || [];
      return userPermissions.includes(permission);
    },
    [state.user]
  );

  /**
   * Check if user has any of the specified permissions (FR-004, FR-047)
   */
  const canAccessAny = useCallback(
    (permissions: string[]): boolean => {
      if (!state.user) {
        return false;
      }

      const userPermissions = permissionsByRole[permissionRole(state.user.role)] || [];
      return permissions.some((p) => userPermissions.includes(p));
    },
    [state.user]
  );

  /**
   * Check if user has all of the specified permissions
   */
  const canAccessAll = useCallback(
    (permissions: string[]): boolean => {
      if (!state.user) {
        return false;
      }

      const userPermissions = permissionsByRole[permissionRole(state.user.role)] || [];
      return permissions.every((p) => userPermissions.includes(p));
    },
    [state.user]
  );

  /**
   * Check if user is an admin (FR-002)
   */
  const isAdmin = useCallback((): boolean => {
    return roleSatisfies(state.user?.role, ['Admin']);
  }, [state.user]);

  /**
   * Check if user is an operator or higher (FR-002)
   */
  const isOperatorOrAbove = useCallback((): boolean => {
    return roleSatisfies(state.user?.role, ['Admin', 'Operator']);
  }, [state.user]);

  /**
   * Check if user is read-only (viewer role) (FR-003, FR-006, FR-007)
   */
  const isReadOnly = useCallback((): boolean => {
    return state.user?.role === 'viewer';
  }, [state.user]);

  /**
   * Get user's permissions list
   */
  const getPermissions = useCallback((): string[] => {
    if (!state.user) {
      return [];
    }
    return permissionsByRole[permissionRole(state.user.role)] || [];
  }, [state.user]);

  return {
    ...state,
    login,
    logout,
    getAccessToken,
    refreshSession,
    hasRole,
    canAccess,
    canAccessAny,
    canAccessAll,
    isAdmin,
    isOperatorOrAbove,
    isReadOnly,
    getPermissions,
  };
}

/**
 * Permission-based visibility helper functions
 * Use these to conditionally render UI elements based on user permissions
 *
 * Requirements:
 * - FR-002: Support roles: admin, operator, viewer
 * - FR-004: Role-based permissions enforcement
 * - FR-006: Read-only users can view backup history
 * - FR-007: Read-only users can view detailed backup information
 * - FR-047: Support read-only access for junior team members
 */

/**
 * UI element visibility rules by feature
 */
export const uiVisibilityRules = {
  // Dashboard is visible to all roles
  dashboard: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
  },

  // Backup features
  backups: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
    trigger: ['admin', 'operator'] as UserRole[],
    cancel: ['admin', 'operator'] as UserRole[],
  },

  // Lakehouse features
  lakehouses: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
    configure: ['admin'] as UserRole[],
    deploy: ['admin'] as UserRole[],
  },

  // Schedule features
  schedules: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
    create: ['admin'] as UserRole[],
    edit: ['admin'] as UserRole[],
    delete: ['admin'] as UserRole[],
  },

  // Alert features
  alerts: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
    acknowledge: ['admin', 'operator'] as UserRole[],
    resolve: ['admin', 'operator'] as UserRole[],
    configure: ['admin'] as UserRole[],
  },

  // Settings features
  settings: {
    view: ['admin', 'operator', 'viewer'] as UserRole[],
    edit: ['admin'] as UserRole[],
  },

  // User management
  users: {
    view: ['admin'] as UserRole[],
    create: ['admin'] as UserRole[],
    edit: ['admin'] as UserRole[],
    delete: ['admin'] as UserRole[],
  },

  // Audit logs
  audit: {
    view: ['admin', 'operator'] as UserRole[],
    export: ['admin'] as UserRole[],
  },
};

/**
 * Check if a role can access a specific UI feature
 */
export function canRoleAccessFeature(
  role: UserRole | undefined,
  feature: keyof typeof uiVisibilityRules,
  action: string
): boolean {
  if (!role) {
    return false;
  }

  const featureRules = uiVisibilityRules[feature];
  if (!featureRules) {
    return false;
  }

  const allowedRoles = (featureRules as Record<string, UserRole[]>)[action];
  if (!allowedRoles) {
    return false;
  }

  return allowedRoles.includes(role);
}

/**
 * Get visible navigation items for a role
 */
export function getVisibleNavItems(role: UserRole | undefined): string[] {
  if (!role) {
    return [];
  }

  const items: string[] = ['dashboard'];

  if (canRoleAccessFeature(role, 'backups', 'view')) {
    items.push('backups');
  }
  if (canRoleAccessFeature(role, 'lakehouses', 'view')) {
    items.push('lakehouses');
  }
  if (canRoleAccessFeature(role, 'schedules', 'view')) {
    items.push('schedules');
  }
  if (canRoleAccessFeature(role, 'alerts', 'view')) {
    items.push('alerts');
  }
  if (canRoleAccessFeature(role, 'settings', 'view')) {
    items.push('settings');
  }
  if (canRoleAccessFeature(role, 'users', 'view')) {
    items.push('users');
  }
  if (canRoleAccessFeature(role, 'audit', 'view')) {
    items.push('audit');
  }

  return items;
}

export default useAuth;
